import { useGesture } from '@use-gesture/react';
import { pasteImageAsFrame } from 'clipboard';
import ZoomTool from 'components/Tool/ZoomTool';
import { getBoundingBox, Point, Rect } from 'dragonmouse-shared/geometry';
import { Frame, FrameType } from 'dragonmouse-shared/interfaces';
import { clamp } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectFrames } from 'redux/db/reducers';
import { canEnhanceFrame, getFrame, getFrameBounds } from 'redux/db/util';
import {
	setCanvasMousePosition,
	setCenterFramesAsChatGeneratesThem,
	setHovered,
	setMouseDragMode,
	setOffset,
	setScaleAroundMousePosition,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { MouseDragMode, selectTransform, ToolType } from 'redux/mode/reducers';
import { framePicked, getCanvasRect, getScenePoint, MAX_SCALE, MIN_SCALE } from 'redux/mode/util';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import { CompleteFunction, TargetCanvas } from 'transfer/Target';
import { TransferDragType } from 'transfer/Transfer';
import { TransferBoard } from 'transfer/TransferBoard';
import { isMac } from 'utils';
import HandTool from '../Tool/HandTool';
import SelectTool from '../Tool/SelectTool';
import FrameControls from './FrameControls';
import * as S from './styles';

export interface BoundedPick {
	bounds: Rect;
	pick: Pick;
	canEnhance: boolean;
}

// all interactive widgets etc that relate to the canvas appear here
const UI = () => {
	const dispatch = useDispatch();

	const {
		activeTool,
		hovered,
		selected,
		selectedFrames,
		frames,
		transform,
		zoomPreviewBox,
		activeSceneId,
		centeredFrameId,
	} = useSelector(
		(r: RootReducer) => ({
			activeTool: r.mode.activeTool,
			hovered: r.mode.hovered,
			selected: r.mode.selected,
			selectedFrames: selectSelectedFrames(r),
			frames: selectFrames(r.db),
			transform: selectTransform(r.mode),
			zoomPreviewBox: r.mode.zoomPreviewBox,
			activeSceneId: r.mode.activeSceneId,
			centeredFrameId: r.mode.centeredFrameId,
		}),
		shallowEqual
	);

	const gestureTarget = useRef() as React.MutableRefObject<HTMLDivElement>;
	const [swipeStartPos, setSwipeStartPos] = useState<Point>({ x: 0, y: 0 });
	const [swipeStartOffset, setSwipeStartOffset] = useState<Point>({ x: 0, y: 0 });

	const [pinching, setPinching] = useState<boolean>(false);
	const [zoomStartScale, setZoomStartScale] = useState<number>(1.0);

	// variables to handle when the user is panning the canvas
	const [panOverride, setPanOverride] = useState<boolean>(false);
	const [panClickPos, setPanClickPos] = useState<Point>({ x: 0, y: 0 });
	const [panClickOffset, setPanClickOffset] = useState<Point>({ x: 0, y: 0 });

	const [mouseIsOver, setMouseIsOver] = useState<boolean>(false);
	const [centeredFrame, setCenteredFrame] = useState<Frame | null>(null);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	const [{ isOver, canDrop }, drop] = useDrop(
		() => ({
			accept: TransferDragType,
			canDrop: (item, monitor) => {
				return getComplete(item, monitor) != null;
			},
			drop: (item, monitor) => completeDrop(item, monitor),
			collect: (monitor) => ({
				isOver: !!monitor.isOver(),
				canDrop: !!monitor.canDrop(),
			}),
		}),
		[transform.scale]
	);
	// make our gestureTarget ref also function as a drop target
	drop(gestureTarget);

	useEffect(() => {
		if (centeredFrameId === null) {
			setCenteredFrame(null);
			return;
		}
		if (centeredFrameId !== centeredFrame?.id) {
			const frame = getFrame(centeredFrameId);
			if (frame) {
				setCenteredFrame(frame);
			}
		}
	}, [centeredFrameId]);

	//------------------------------------------------------------------------------------------------
	// elements to draw
	// these are stored in scene coordinates
	//------------------------------------------------------------------------------------------------

	// selection rects
	const [selections, selectionBoundingBox] = useMemo(() => {
		const collectedBounds: BoundedPick[] = [];
		for (const pick of selected) {
			if (pick.isFrame()) {
				const frame = getFrame(pick.frameId);
				if (frame) {
					collectedBounds.push({
						bounds: getFrameBounds(frame),
						pick,
						canEnhance: canEnhanceFrame(frame),
					});
				}
			}
		}
		return [collectedBounds, getBoundingBox(collectedBounds.map((s) => s.bounds))];
	}, [frames, selected]);

	// hover rects
	const hovers: BoundedPick[] = useMemo(() => {
		if (!hovered) return [];

		if (hovered.isFrame()) {
			const frame = getFrame(hovered.frameId);
			if (!frame) return [];

			// if it's already selected, don't show a hover state
			if (framePicked(selected, frame.id)) return [];

			return [
				{
					bounds: getFrameBounds(frame),
					pick: hovered,
					canEnhance: canEnhanceFrame(frame),
				},
			];
		}

		return [];
	}, [frames, selected, hovered]);

	useGesture(
		{
			// This code processes 2-finger swipes on Mac trackpad, and is optimized for
			// that use case. Also ends up supporting scroll wheels somewhat OK.
			onWheel: ({ event, first, last, offset: [x, y], movement: [_mx, my], metaKey, ctrlKey }) => {
				event.preventDefault();
				if (pinching) return;

				// When meta or ctrl keys are down, translate vertical scrolling into zoom instead
				if (metaKey || ctrlKey) {
					// On Mac, pinch-zooming sends both onPinch and onWheel events (both with ctrlKey true)
					// In spiderbox, we supported pinch-zooming here with the onWheel, but in these
					// dragonmouse days, we're using the trackpad more seriously and a glitch arised with
					// inertial scrolling from a 2-finger swipe affecting the data that goes through onWheel,
					// thus frustrating our best efforts to make pinch-zooming work here.
					// So I decided not to handle it here and to handle it in the onPinch instead - ss
					if (isMac() && ctrlKey) return;

					// What to multiply by scrolling magnitudes to get zoom magnitudes.
					const scrollToZoomTranslation = 0.0015;

					let newScale = zoomStartScale - my * scrollToZoomTranslation;
					if (first) {
						setZoomStartScale(transform.scale);
						// For the first event, ignore the start since it will be for last time.
						newScale = transform.scale - my * scrollToZoomTranslation;
					}
					dispatch(setCenterFramesAsChatGeneratesThem(false));
					dispatch(setScaleAroundMousePosition(clamp(newScale, MIN_SCALE, MAX_SCALE)));
					return;
				}

				// When no modifier keys are held down, scroll.
				if (first) {
					setSwipeStartOffset(transform.offset);
					setSwipeStartPos({ x, y });
					dispatch(setMouseDragMode(MouseDragMode.UI));
				} else {
					if (last) {
						dispatch(setMouseDragMode(MouseDragMode.NONE));
					}
					const dx = swipeStartPos.x - x;
					const dy = swipeStartPos.y - y;

					dispatch(setCenterFramesAsChatGeneratesThem(false));
					dispatch(
						setOffset({
							x: swipeStartOffset.x + dx,
							y: swipeStartOffset.y + dy,
						})
					);
				}
			},
			// process pinches on the trackpad
			onPinch: ({ event, first, last, offset: [scale, _angle], metaKey, ctrlKey }) => {
				event.preventDefault();

				// on PCs, meta/ctrl should be false during pinch zooming, so don't do anything
				if (!isMac()) {
					if (metaKey || ctrlKey) return;
				}
				if (first) {
					setZoomStartScale(transform.scale);
					setPinching(true);
				} else if (last) {
					setPinching(false);
					return;
				} else {
					// often on the first event scale is errant on Mac (or something weird is happening not usre exactly why)
					// often on the last event, we get an errant scale value
					// so we only update when neither first nor last is true

					// when using the mouse wheel, the scale multiplier (called "scale" in the event)
					// can be quite large and quite small. let's clamp to prevent jerky zooming
					// note you can STILL get bad behavior with the mouse wheel, but it's less likely
					//
					// this code warrants more work but for now I'm confident this is an improvement
					// I actually think there's something fundamentally broken about using onPinch for our
					// wheel-zoom needs and we need to find a way to have this handled instead by onWheel - ss
					const scaleMultiplier = clamp(scale, 0.1, 5);
					const newScale = scaleMultiplier * zoomStartScale;
					dispatch(setCenterFramesAsChatGeneratesThem(false));
					dispatch(setScaleAroundMousePosition(clamp(newScale, MIN_SCALE, MAX_SCALE)));
				}
			},
		},
		{
			target: gestureTarget,
			pinch: { from: [1, 1] }, // specifying "from" causes scale to reset each time a pinch gesture is initiated.
			wheel: { eventOptions: { passive: false } },
		}
	);

	// Add file drop handling
	useEffect(() => {
		const handleDragOver = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
		};

		const handleDrop = async (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();

			const files = Array.from(e.dataTransfer?.files || []);
			const imageFiles = files.filter((file) => file.type.startsWith('image/'));

			if (imageFiles.length > 0) {
				if (!activeSceneId) return;

				const rect = (e.currentTarget as HTMLElement)?.getBoundingClientRect();
				if (!rect) return;

				// Convert drop coordinates to scene coordinates
				const scenePoint = getScenePoint({
					x: e.clientX - rect.left,
					y: e.clientY - rect.top,
				});

				for (const file of imageFiles) {
					await pasteImageAsFrame(
						{
							types: [file.type],
							getType: async () => file,
							presentationStyle: 'inline',
						},
						activeSceneId,
						scenePoint
					);
				}
			}
		};

		const element = gestureTarget.current;
		if (element) {
			element.addEventListener('dragover', handleDragOver);
			element.addEventListener('drop', handleDrop);
		}

		return () => {
			if (element) {
				element.removeEventListener('dragover', handleDragOver);
				element.removeEventListener('drop', handleDrop);
			}
		};
	}, [activeSceneId]);

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	// Return the most appropriate complete function for the provided item (if at least one is available)
	const getComplete = (item: any, monitor: DropTargetMonitor): CompleteFunction | null => {
		const canvas = monitor.getClientOffset();
		if (!canvas) return null;

		if (!(item instanceof TransferBoard)) return null;
		const board: TransferBoard = item;

		const scene = getScenePoint(canvas);

		const target = new TargetCanvas(scene);

		// try to process transfers in order until we've found one
		for (const transfer of board.transfers) {
			const complete = target.getCompleteTransfer(transfer, board);
			if (complete) {
				return complete;
			}
		}
		return null;
	};

	const completeDrop = (item: any, monitor: DropTargetMonitor) => {
		const complete = getComplete(item, monitor);
		if (complete) complete();
	};

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onMouseMove = (event: any) => {
		if (event as MouseEvent) {
			if (panOverride) {
				const dx = event.pageX - panClickPos.x;
				const dy = event.pageY - panClickPos.y;

				dispatch(
					setOffset({
						x: panClickOffset.x + dx,
						y: panClickOffset.y + dy,
					})
				);
			} else {
				const rect = gestureTarget.current.getBoundingClientRect();
				dispatch(
					setCanvasMousePosition({ x: event.clientX - rect.left, y: event.clientY - rect.top })
				);
			}
		}
	};

	const onMouseDown = (event: any) => {
		if ((event as MouseEvent) && event.button === 1) {
			setPanOverride(true);
			setPanClickOffset(transform.offset);
			setPanClickPos({ x: event.pageX, y: event.pageY });
		}
	};

	const onMouseUp = (event: any) => {
		if ((event as MouseEvent) && event.button === 1) {
			setPanOverride(false);
		}
	};

	const onMouseLeave = (_event: any) => {
		setMouseIsOver(false);
		dispatch(setHovered(null));
		setPanOverride(false);
	};

	const onMouseEnter = (_event: any) => {
		setMouseIsOver(true);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderActiveTool = () => {
		if (panOverride) return <S.HandTool />;
		else {
			switch (activeTool) {
				case ToolType.SELECT:
					return <SelectTool />;
				case ToolType.HAND:
					return <HandTool />;
				case ToolType.ZOOM:
					return <ZoomTool />;
				default:
					return null;
			}
		}
	};

	const styleFromRect = (rect: Rect | undefined) => {
		if (!rect) return {};
		return {
			left: rect.x,
			top: rect.y,
			width: rect.width,
			height: rect.height,
		};
	};

	const renderSelection = () => {
		if (centeredFrame) {
			return <FrameControls frame={centeredFrame} />;
		}

		enum ControlType {
			PROTOTYPE,
			FRAME,
			NONE,
		}
		let controlType = ControlType.NONE;
		if (
			selections.length === 1 &&
			selectedFrames.length === 1 &&
			selectedFrames[0].metadata.type &&
			transform.scale > 0.1
		) {
			switch (selectedFrames[0].metadata.type) {
				case FrameType.PROTOTYPE:
					controlType = ControlType.PROTOTYPE;
					break;
				case FrameType.MERGE:
				case FrameType.GENERATE:
				case FrameType.GENERATE_FROM_CHAT:
				case FrameType.ENHANCE:
				case FrameType.ADJUST:
				case FrameType.IMPORT_SITE:
					controlType = ControlType.FRAME;
					break;
				case FrameType.IMPORT:
				default:
					controlType = ControlType.NONE;
					break;
			}
		}
		return selections.map((selection, index) => {
			const canvasRect = getCanvasRect(selection.bounds);

			return (
				<React.Fragment key={index}>
					<S.SelectionBox style={styleFromRect(canvasRect)} />
				</React.Fragment>
			);
		});
	};
	const renderSelectionBoundingBox = () => {
		if (centeredFrame) return null;

		if (selectionBoundingBox.width === 0 && selectionBoundingBox.height === 0) return null;
		const canvasRect = getCanvasRect(selectionBoundingBox);
		return <S.SelectionBoundingBox style={styleFromRect(canvasRect)} />;
	};

	const renderHover = () => {
		return hovers.map((hover, index) => {
			return <S.HoverBox key={index} style={styleFromRect(getCanvasRect(hover.bounds))} />;
		});
	};

	const renderZoomPreviewBox = () => {
		if (!zoomPreviewBox) return null;
		return <S.ZoomPreviewBox style={styleFromRect(zoomPreviewBox)} />;
	};

	return (
		<S.UI
			ref={gestureTarget}
			onMouseMove={onMouseMove}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			onMouseLeave={onMouseLeave}
			onMouseEnter={onMouseEnter}
			isDropTarget={isOver && canDrop}
		>
			{renderActiveTool()}
			{renderSelection()}
			{renderSelectionBoundingBox()}
			{mouseIsOver && renderHover()}
			{renderZoomPreviewBox()}
		</S.UI>
	);
};

export default UI;
